import React, {useState} from 'react';
import {Alert, Box, Button, TextField} from '@mui/material';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {NumberFormatValues, NumericFormat} from 'react-number-format';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {Panel} from '../../components/Panel/Panel';
import {AmlKycContact} from '../../schema-types/AmlKycContact';
import {AmlKycOptions_amlKycOptions} from '../../schema-types/AmlKycOptions';
import {UpdateAmlKyc, UpdateAmlKycVariables} from '../../schema-types/UpdateAmlKyc';
import {getErrorMessage} from '../../utils/getErrorMessage';
import validateUUID from '../../utils/validateUUID';
import {UPDATE_AML_KYC} from './amlKycQueries';

const MINIMUM_RETIREMENT_AGE = 21;

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called after Curo accepted the change, before the modal closes. */
  onSaved: () => void;
  accountId: string;
  contact: AmlKycContact;
  options: AmlKycOptions_amlKycOptions;
};

type FormValues = {
  nationality: string;
  dualNationality: string;
  employmentStatus: string;
  hubwiseOccupation: string;
  hubwiseIndustry: string;
  annualIncome: string;
  retirementAge: string;
};

const toFormValues = (contact: AmlKycContact): FormValues => ({
  nationality: contact.nationality ?? '',
  dualNationality: contact.dualNationality ?? '',
  employmentStatus: contact.employmentStatus ?? '',
  hubwiseOccupation: contact.hubwiseOccupation ?? '',
  hubwiseIndustry: contact.hubwiseIndustry ?? '',
  annualIncome: contact.annualIncome ?? '',
  retirementAge: contact.retirementAge === null ? '' : `${contact.retirementAge}`,
});

const optionalWhenNotWorking = (label: string, notWorking: boolean) =>
  notWorking ? label : `${label} *`;

const CANNOT_CLEAR = 'Cannot be cleared once saved to Curo.';

// Curo rejects a blank picklist or number, so once one is saved it can only be changed.
const blankOption = (saved: string | null, prompt: string) =>
  saved ? null : <option value="">{prompt}</option>;

const clearedError = (saved: string | number | null, value: string) =>
  saved !== null && value === '' ? CANNOT_CLEAR : undefined;

/**
 * Hubwise AML/KYC capture dialog (DBO-1584): seven contact fields, written to
 * Curo in the same request so a rejection is shown here and nothing is left
 * half-saved.
 */
export const AmlKycModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  accountId,
  contact,
  options,
}) => {
  const [form, setForm] = useState<FormValues>(() => toFormValues(contact));
  const [errors, setErrors] = useState<string[]>([]);
  const {enqueueSnackbar} = useSnackbar();
  const [updateAmlKyc, {loading}] = useMutation<UpdateAmlKyc, UpdateAmlKycVariables>(
    UPDATE_AML_KYC
  );

  const setField =
    (field: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((previous) => ({...previous, [field]: event.target.value}));

  const setNumberField =
    (field: keyof FormValues) =>
    ({value}: NumberFormatValues) =>
      setForm((previous) => ({...previous, [field]: value}));

  const notWorking = options.notWorkingStatuses.includes(form.employmentStatus);
  const retirementAge = form.retirementAge === '' ? null : Number(form.retirementAge);
  const retirementAgeError =
    retirementAge !== null && retirementAge < MINIMUM_RETIREMENT_AGE
      ? `Must be ${MINIMUM_RETIREMENT_AGE} or over.`
      : clearedError(contact.retirementAge, form.retirementAge);
  const annualIncomeError = clearedError(contact.annualIncome, form.annualIncome);
  const canSubmit = !loading && !retirementAgeError && !annualIncomeError;

  // Curo may hold a code OneView's current list no longer offers; keep it selectable until changed.
  const nationalityOptions = (current: string) =>
    current && !options.nationalities.some((nationality) => nationality.code === current)
      ? [{code: current, label: current}, ...options.nationalities]
      : options.nationalities;

  const submit = async () => {
    setErrors([]);
    try {
      const {data} = await updateAmlKyc({
        variables: {
          accountId: validateUUID(accountId)!,
          contactId: validateUUID(contact.id)!,
          nationality: form.nationality || null,
          dualNationality: form.dualNationality || null,
          employmentStatus: form.employmentStatus || null,
          hubwiseOccupation: form.hubwiseOccupation || null,
          hubwiseIndustry: form.hubwiseIndustry || null,
          annualIncome: form.annualIncome || null,
          retirementAge,
        },
      });
      const result = data?.updateAmlKyc;
      if (!result?.ok) {
        setErrors(
          result?.errors?.length ? result.errors : ['Could not save the AML/KYC details.']
        );
        return;
      }
      enqueueSnackbar('AML/KYC details saved to Curo.', {variant: 'success'});
      onSaved();
      onClose();
    } catch (error) {
      setErrors([getErrorMessage(error, 'Could not reach the server.')]);
    }
  };

  return (
    <ScrollableModal size="small" autoHeight open={open} onClose={onClose}>
      <Panel title={`AML/KYC details: ${contact.simplifiedName || 'contact'}`}>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            id="aml-kyc-nationality"
            select
            SelectProps={{native: true}}
            label="Nationality *"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            value={form.nationality}
            onChange={setField('nationality')}
          >
            <option value="">Select a nationality</option>
            {nationalityOptions(form.nationality).map((nationality) => (
              <option key={nationality.code} value={nationality.code}>
                {nationality.label}
              </option>
            ))}
          </TextField>
          <TextField
            id="aml-kyc-dual-nationality"
            select
            SelectProps={{native: true}}
            label="Dual nationality"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            value={form.dualNationality}
            onChange={setField('dualNationality')}
          >
            <option value="">Select a nationality</option>
            {nationalityOptions(form.dualNationality).map((nationality) => (
              <option key={nationality.code} value={nationality.code}>
                {nationality.label}
              </option>
            ))}
          </TextField>
          <TextField
            id="aml-kyc-employment-status"
            select
            SelectProps={{native: true}}
            label="Employment status *"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            value={form.employmentStatus}
            onChange={setField('employmentStatus')}
          >
            {blankOption(contact.employmentStatus, 'Select an employment status')}
            {options.employmentStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </TextField>
          <TextField
            id="aml-kyc-occupation"
            select
            SelectProps={{native: true}}
            label={optionalWhenNotWorking('Occupation', notWorking)}
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            value={form.hubwiseOccupation}
            onChange={setField('hubwiseOccupation')}
          >
            {blankOption(contact.hubwiseOccupation, 'Select an occupation')}
            {options.occupations.map((occupation) => (
              <option key={occupation} value={occupation}>
                {occupation}
              </option>
            ))}
          </TextField>
          <TextField
            id="aml-kyc-industry"
            select
            SelectProps={{native: true}}
            label={optionalWhenNotWorking('Industry', notWorking)}
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            value={form.hubwiseIndustry}
            onChange={setField('hubwiseIndustry')}
          >
            {blankOption(contact.hubwiseIndustry, 'Select an industry')}
            {options.industries.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </TextField>
          <NumericFormat
            customInput={TextField}
            id="aml-kyc-annual-income"
            label="Annual income *"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            prefix="£"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            valueIsNumericString
            value={form.annualIncome}
            onValueChange={setNumberField('annualIncome')}
            error={Boolean(annualIncomeError)}
            helperText={annualIncomeError}
          />
          <NumericFormat
            customInput={TextField}
            id="aml-kyc-retirement-age"
            label="Retirement age *"
            fullWidth
            size="small"
            InputLabelProps={{shrink: true}}
            decimalScale={0}
            allowNegative={false}
            valueIsNumericString
            value={form.retirementAge}
            onValueChange={setNumberField('retirementAge')}
            error={Boolean(retirementAgeError)}
            helperText={retirementAgeError}
          />
        </Box>
        {errors.length > 0 ? (
          <Alert severity="error" sx={{mt: 2}}>
            {errors.join(' ')}
          </Alert>
        ) : null}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="text" onClick={onClose} disabled={loading}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            onClick={submit}
          >
            Save
          </Button>
        </Box>
      </Panel>
    </ScrollableModal>
  );
};
